import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import iconIA from 'assets/images/ia/icon-visao.svg';
import Loader from 'components/atoms/loader';
import EmptyState from 'components/atoms/empty_state';
import IconActionButton from 'components/atoms/icon_action_button';
import TradingMessages from 'components/molecules/trading_messages';
import ChatMessageSearch from 'components/organisms/chat_message_search';
import MonitoringSellingPage from 'components/organisms/monitoring_selling_page';
import OverviewControl from 'components/organisms/overview_control';
import ComplementaryServices from 'services/complementary_services';
import companyId from 'services/company_id';
import { ApplicationState } from 'store';
import { BiddingData } from 'store/modules/bidding_store/types';
import { getBiddingOverview } from 'store/modules/management_store/actions';
import { ManagementState } from 'store/modules/management_store/types';
import { getElectronicTrading, getTradingMessages } from 'store/modules/monitoring_store/actions';
import { MonitoringState, TradingMessageTypes } from 'store/modules/monitoring_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import { getServiceSubscriptionStatus } from 'store/modules/service_subscription_store/actions';
import { ServicesSubscriptionState } from 'store/modules/service_subscription_store/types';
import { Container } from './styles';

const MONITORING_SERVICE_ID = ComplementaryServices.find(
  (service) => service.name === 'Monitoring',
)?.id;
const OVERVIEW_CACHE_TIME = 5 * 60 * 1000;

interface AiManagementBiddingChatProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
}

const AiManagementBiddingChat: React.FC<AiManagementBiddingChatProps> = ({
  bidding,
  loading,
  error,
}) => {
  const [requested, setRequested] = useState(false);
  const [showSearchMessage, setShowSearchMessage] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [messageSearch, setMessageSearch] = useState('');
  const [scrollTargetMessageId, setScrollTargetMessageId] = useState<number | null>(null);
  const [searchResults, setSearchResults] = useState<TradingMessageTypes[]>([]);
  const dispatch = useDispatch();

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { monitoring, request: monitoringRequest } = useSelector<ApplicationState, MonitoringState>(
    (state) => state.storage.monitoring,
  );
  const { servicesSubscriptions } = useSelector<ApplicationState, ServicesSubscriptionState>(
    (state) => state.storage.servicesSubscriptions,
  );
  const { management, request: managementRequest } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const electronicTradingId = bidding?.has_electronic_trading
    ? bidding.electronic_trading?.id
    : undefined;
  const trading = monitoring.electronics_tradings.find((item) => item.id === electronicTradingId);
  const biddingId = bidding ? Number(bidding.bidding_id) || Number(bidding.id) : undefined;
  const monitoringSubscription = servicesSubscriptions.Monitoring;
  const withoutSubscription = !!monitoringSubscription && !monitoringSubscription.current_signature;

  useEffect(() => {
    if (!electronicTradingId) return;

    if (MONITORING_SERVICE_ID)
      dispatch(getServiceSubscriptionStatus(MONITORING_SERVICE_ID, companyId(profile)));
    dispatch(getElectronicTrading(electronicTradingId));
    dispatch(getTradingMessages(electronicTradingId));
    setRequested(true);
  }, [electronicTradingId]);

  useEffect(() => {
    if (!overviewOpen || !biddingId) return;

    const biddingChanged = management.overview_bidding_id !== biddingId;
    const cacheExpired =
      !management.overview ||
      Date.now() - (management.overview_last_updated || 0) > OVERVIEW_CACHE_TIME;

    if (biddingChanged || cacheExpired) dispatch(getBiddingOverview(biddingId));
  }, [overviewOpen, biddingId]);

  const openSearch = () => {
    setOverviewOpen(false);
    setShowSearchMessage(true);
  };

  const toggleOverview = () => {
    setShowSearchMessage(false);
    setOverviewOpen((open) => !open);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  if (error || !bidding) {
    return (
      <div className="alert alert-warning m-4" role="alert">
        Não foi possível carregar as mensagens do chat.
      </div>
    );
  }

  const renderChat = () => {
    if (!electronicTradingId) {
      return (
        <EmptyState
          icon="forum"
          title="Esta licitação não possui chat"
          description="O chat aparece para licitações com pregão eletrônico em portais monitorados."
        />
      );
    }

    if (withoutSubscription) {
      return (
        <div className="flex-grow-1 overflow-auto">
          <MonitoringSellingPage />
        </div>
      );
    }

    if (!trading) {
      if (!requested || monitoringRequest.electronics_tradings.loading) {
        return (
          <div className="d-flex justify-content-center py-5">
            <Loader />
          </div>
        );
      }

      return (
        <div className="alert alert-warning mx-4" role="alert">
          Não foi possível carregar o chat desta licitação.
        </div>
      );
    }

    return (
      <TradingMessages
        selectedTradingId={trading.id}
        messageSearch={messageSearch}
        scrollTargetMessageId={scrollTargetMessageId}
        setSearchResults={setSearchResults}
        showTitle={false}
      />
    );
  };

  return (
    <Container className="d-flex">
      <div className="chat-main d-flex flex-column flex-grow-1">
        <div className="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
          <h4 className="mb-0">Mensagens do chat</h4>
          {trading && !withoutSubscription && (
            <div className="d-flex align-items-center">
              {trading.have_messages && (
                <button
                  type="button"
                  className="chat-action-btn btn btn-sm btn-ia rounded databalloon d-flex justify-content-center align-items-center mx-1"
                  aria-label="Visão geral"
                  data-balloon-pos="left"
                  data-balloon-blunt
                  onClick={toggleOverview}
                >
                  <img alt="Ícone IA" src={iconIA} height="18" />
                </button>
              )}
              <IconActionButton
                icon="refresh"
                variant="secondary"
                className="chat-action-btn rounded"
                ariaLabel="Buscar atualizações"
                onClick={() => dispatch(getTradingMessages(trading.id))}
              />
              <IconActionButton
                icon="search"
                variant="secondary"
                className="chat-action-btn rounded"
                ariaLabel="Pesquisar mensagens"
                onClick={openSearch}
              />
            </div>
          )}
        </div>

        {renderChat()}
      </div>

      {showSearchMessage && (
        <aside className="chat-search-pane flex-shrink-0">
          <ChatMessageSearch
            setShowSearchMessage={setShowSearchMessage}
            keywords={monitoring.trading_keywords[companyId(profile)]}
            setMessageSearch={setMessageSearch}
            setScrollTargetMessageId={setScrollTargetMessageId}
            searchResults={searchResults}
          />
        </aside>
      )}

      {overviewOpen && (
        <aside className="chat-overview-pane flex-shrink-0 border-left">
          <OverviewControl
            inline
            loading={managementRequest?.get_bidding_overview?.loading}
            onClose={() => setOverviewOpen(false)}
            sections={[
              { type: 'chat', data: management?.overview?.chat_principais },
              { type: 'andamentos', data: management?.overview?.andamentos },
              { type: 'anotacoes', data: management?.overview?.anotacoes },
            ]}
          />
        </aside>
      )}
    </Container>
  );
};

export default AiManagementBiddingChat;
